import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { defaultIfEmpty, take } from 'rxjs/operators';
import { wellFormedBlocks } from './assistant-chat.blocks';
import { AssistantChatResponder } from './assistant-chat.responder';
import {
  AssistantChatAnswered,
  AssistantChatBlock,
  AssistantChatMessage,
  AssistantChatPrompt,
  AssistantChatRating,
  AssistantChatReply,
  AssistantChatStage,
} from '../../interfaces/assistant-chat.interface';

export interface AssistantChatState {
  threadId: string;
  messages: AssistantChatMessage[];
  stage: AssistantChatStage;
  typing: boolean;
}

function newThreadId(): string {
  const random = (globalThis as any).crypto?.randomUUID?.();
  return random || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface AssistantChatReplies {
  empty: string;
  failed: string;
  abandoned: string;
}

export interface AssistantChatFailure {
  error: string;
}

const DEFAULT_REPLIES: AssistantChatReplies = {
  empty: 'The answer came back empty.',
  failed: 'The answer could not be written.',
  abandoned: 'The question was left unanswered because the page changed.',
};

@Injectable()
export class AssistantChatService implements OnDestroy {
  public readonly answered$ = new Subject<AssistantChatAnswered>();

  private readonly responder = inject(AssistantChatResponder, { optional: true });
  private readonly stateSubject = new BehaviorSubject<AssistantChatState>({
    threadId: newThreadId(),
    messages: [],
    stage: 'choices',
    typing: false,
  });

  private answerSubscription: Subscription;
  private replies: AssistantChatReplies = DEFAULT_REPLIES;

  public get state(): AssistantChatState {
    return this.stateSubject.value;
  }

  public get state$(): Observable<AssistantChatState> {
    return this.stateSubject.asObservable();
  }

  public ngOnDestroy(): void {
    this.answerSubscription?.unsubscribe();
  }

  public ask(prompt: AssistantChatPrompt, replies: AssistantChatReplies = DEFAULT_REPLIES): boolean {
    if (this.state.typing) {
      return false;
    }

    this.replies = replies;

    const thread = this.state.messages;
    this.patch({
      messages: [
        ...thread,
        {
          role: 'user',
          text: prompt.text,
          meta: { askedAt: Date.now(), origin: prompt.origin, suggestionId: prompt.suggestionId },
        },
      ],
      typing: true,
    });

    if (!this.responder) {
      this.answerWithFailure([]);
      return true;
    }

    this.answerSubscription?.unsubscribe();
    this.answerSubscription = this.responder.ask(prompt, thread).pipe(
      take(1),
      defaultIfEmpty({ blocks: [] } as AssistantChatReply),
    ).subscribe({
      next: (reply) => {
        const { blocks, requestId, failed } = Array.isArray(reply) ? { blocks: reply } as AssistantChatReply : reply || { blocks: [] };
        if (failed) {
          this.answerWithFailure(blocks, requestId);
        } else {
          this.answer(blocks, requestId);
        }
      },
      error: (reason) => {
        this.answerWithFailure([{ type: 'text', body: this.failureFrom(reason) }]);
      },
    });

    return true;
  }

  public answer(blocks: AssistantChatBlock[], requestId?: string): void {
    const given = wellFormedBlocks(blocks).filter(block => block.type !== 'working');
    if (!given.length) {
      this.answerWithFailure([{ type: 'text', body: this.replies.empty }], requestId);
      return;
    }

    this.append(given, requestId, false);
  }

  public answerWithFailure(blocks: AssistantChatBlock[], requestId?: string): void {
    const wellFormed = wellFormedBlocks(blocks);
    const given = wellFormed.length ? wellFormed : [{ type: 'text', body: this.replies.failed } as AssistantChatBlock];
    this.append(given, requestId, true);
  }

  public rate(index: number, rating: AssistantChatRating): boolean {
    const message = this.state.messages[index];
    if (!message || message.role !== 'assistant') {
      return false;
    }

    const messages = this.state.messages.map((each, position) =>
      position === index ? { ...each, meta: { ...(each.meta || {}), rating } } : each,
    );
    this.patch({ messages });
    return true;
  }

  public openFreeText(): void {
    this.patch({ stage: 'guided' });
  }

  public abandonAnswer(): void {
    this.answerSubscription?.unsubscribe();
    if (this.state.typing) {
      this.answerWithFailure([{ type: 'text', body: this.replies.abandoned }]);
    }
  }

  public restart(): boolean {
    if (this.state.typing) {
      return false;
    }

    this.answerSubscription?.unsubscribe();
    this.stateSubject.next({ threadId: newThreadId(), messages: [], stage: 'choices', typing: false });
    return true;
  }

  private append(blocks: AssistantChatBlock[], requestId: string | undefined, failed: boolean): void {
    if (!this.state.typing) {
      return;
    }

    const question = [...this.state.messages].reverse().find(message => message.role === 'user');
    const answeredAt = Date.now();
    const latencyMs = question?.meta?.askedAt ? answeredAt - question.meta.askedAt : undefined;
    const meta = { answeredAt, latencyMs, requestId, failed };
    const index = this.state.messages.length;
    this.patch({
      messages: [...this.state.messages, { role: 'assistant', blocks, meta }],
      stage: 'open',
      typing: false,
    });
    this.answered$.next({
      index,
      requestId,
      latencyMs,
      failed,
      sectionTypes: blocks.map(block => block.type),
      followups: blocks.filter(block => block.type === 'followups').reduce((n, block) => n + (block.type === 'followups' ? block.items.length : 0), 0),
      question: question?.text,
      origin: question?.meta?.origin,
      suggestionId: question?.meta?.suggestionId,
    });
  }

  private failureFrom(reason: unknown): string {
    const given = (reason as AssistantChatFailure | null | undefined)?.error;
    const body = typeof given === 'string' ? given.trim() : '';
    return body || this.replies.failed;
  }

  private patch(change: Partial<AssistantChatState>): void {
    this.stateSubject.next({ ...this.state, ...change });
  }
}
